import {
  Col,
  Flex,
  Form,
  FormRule,
  Grid,
  Input,
  message,
  Radio,
  Row,
  Typography,
} from 'antd';
import { stickyTop } from '@/components/layout/MobileHeader/stickyTop';
import React, { FC, Fragment, useEffect, useState } from 'react';
import { FloatingLabel } from 'src/components/ui/FloatingLabel';
import { Hint } from '../assets/Hint';
import { CustomButton } from 'src/components/ui';
import { Header } from '../assets/Header';
import { updateProfile } from '@/api/user';
import { useMutation } from '@tanstack/react-query';
import { BackButton } from '@/components/shared';
import { ROUTES } from '@/types/routes';
import { useNavigate } from 'react-router';
import queryClient from '@/client/queryClient';
import { Profile } from '@/pages/my-profile/general';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectProfileCompleteness, selectUser } from '@/slice/user';
import { CustomTextarea } from '@/components/ui/CustomTextarea';
import Blocker from '@/components/blocker';
import { ProfileProgressBar } from '@/components/shared/profile-progress-bar';
import './index.scss';

interface FormItem {
  name: string;
  label: string;
  type: 'input' | 'textarea' | 'yes-no';
  rules?: FormRule[];
}

export const TrainingAndSupport: FC = () => {
  const [form] = Form.useForm();

  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const profileCompleteness = useAppSelector(selectProfileCompleteness);
  const [formChanged, setFormChanged] = useState(false);

  const { lg } = Grid.useBreakpoint();

  useEffect(() => {
    form.setFieldsValue(user);
  }, [form, user]);

  const renderFormItem = (item: FormItem) => {
    switch (item.type) {
      case 'input':
        return <Input size={'large'} />;

      case 'textarea':
        return <CustomTextarea />;
      case 'yes-no':
        return (
          <Radio.Group
            style={{ marginTop: 24, marginLeft: 24 }}
            options={[
              {
                value: true,
                label: 'Yes',
              },
              {
                value: false,
                label: 'No',
              },
            ]}
          />
        );
    }
  };

  const formFields: FormItem[] = [
    {
      name: 'financialAssistance',
      label: 'Financial Assistance',
      type: 'yes-no',
    },
    {
      name: 'financialAssistanceDetails',
      label: 'Additional Financial Assistance Details (Optional)',
      type: 'textarea',
    },
    {
      name: 'siteSelectionAssistance',
      label: 'Site Selection Assistance',
      type: 'yes-no',
    },
    {
      name: 'leaseNegotiationAssistance',
      label: 'Lease Negotiation Assistance',
      type: 'yes-no',
    },
    {
      name: 'recruitmentAssistance',
      label: 'Recruitment Assistance',
      type: 'yes-no',
    },
    {
      name: 'coopAdvertising',
      label: 'Cooperative Advertising',
      type: 'yes-no',
    },
    {
      name: 'training',
      label: 'Training',
      type: 'textarea',
    },
    {
      name: 'dayInTheLifeOfTheOwner',
      label: 'Day in the Life of the Owner',
      type: 'textarea',
    },
    {
      name: 'whoIsAnIdealCandidate',
      label: 'Who is an Ideal Candidate',
      type: 'textarea',
    },
  ];

  const profileMutation = useMutation({
    mutationFn: updateProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['profile'],
      });
      message.success('Profile updated successfully');
    },
    onError: (error: any) => {
      message.error(
        error.response.data.message ||
          'Failed to update profile. Please try again.',
      );
    },
  });

  const onFinish = (values: Partial<Profile>) => {
    const data: Partial<Profile> = {};

    Object.keys(values).forEach((key) => {
      const typedKey = key as keyof Profile;
      const value = values[typedKey];
      if (value !== undefined && value !== user[typedKey]) {
        (data as any)[typedKey] = value;
      }
    });

    profileMutation.mutate({
      data,
    });

    setFormChanged(false);
  };

  return (
    <Fragment>
      <Blocker formChanged={formChanged} onFinish={onFinish} form={form} />
      <Row className={'my-profile-contact'}>
        {!lg && (
          <Col span={24}>
            <BackButton />
          </Col>
        )}

        <Col span={24} lg={{ span: 12 }} className={'header'}>
          <Header title={'Training and Support'} />
        </Col>

        <Col
          span={24}
          lg={{ span: 12 }}
          // Desktop: floats in the sticky right column. Phones/tablets: a
          // sticky slot that reserves the bar's height under the subtitle,
          // so it never covers a section title but still follows the scroll.
          style={
            lg
              ? { position: 'sticky', top: stickyTop(24), zIndex: 998 }
              : {
                  position: 'sticky',
                  top: stickyTop(16),
                  zIndex: 998,
                  marginBottom: 24,
                }
          }
        >
          <ProfileProgressBar
            align={'top'}
            steps={profileCompleteness}
            inline={!lg}
          />
        </Col>

        <Col span={24} lg={{ span: 12 }}>
          <Form
            form={form}
            onFinish={onFinish}
            layout={'vertical'}
            requiredMark={false}
            onValuesChange={() => {
              setFormChanged(true);
            }}
            className={'change-password-form'}
          >
            {formFields.map((item) => (
              <FloatingLabel
                childrenType={item.type === 'textarea' ? 'textarea' : 'input'}
                label={item.label}
                key={`${item.name}`}
              >
                <Form.Item
                  rules={item.rules}
                  name={item.name}
                  label={item.label}
                  labelCol={{ flex: 'none' }}
                  wrapperCol={{ span: 24 }}
                >
                  {renderFormItem(item)}
                </Form.Item>
              </FloatingLabel>
            ))}

            <Flex vertical gap={8} style={{ marginTop: 'auto' }}>
              {!lg && (
                <CustomButton
                  type={'default'}
                  text={'CANCEL'}
                  onClick={() => navigate(ROUTES.MY_PROFILE)}
                />
              )}

              <Form.Item noStyle>
                <CustomButton
                  disabled={!formChanged}
                  htmlType={'submit'}
                  type={'primary'}
                  text={'SAVE CHANGES'}
                />
              </Form.Item>
            </Flex>
          </Form>
        </Col>

        {lg && (
          <Col span={12}>
            <Hint
              wrapperStyle={{
                marginLeft: 'auto',
              }}
              hint={`This section provides details about the support and training offered to franchisees.`}
            />
          </Col>
        )}
      </Row>
    </Fragment>
  );
};
